'use client';
import React, { useState } from 'react';
import { Zap, Palette, LayoutTemplate, DollarSign, Printer, Share2 } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const features = [
  {
    id: 'feat-easy',
    icon: Zap,
    emoji: '⚡',
    color: '#f59e0b',
    colorLight: 'rgba(245,158,11,0.12)',
    colorBorder: 'rgba(245,158,11,0.25)',
    tag: 'No learning curve',
    title: 'Ready in 30 minutes',
    description:
      'Our guided builder walks you through every section — cover, cast list, bios, ads. No design skills, no blank-page panic.',
    size: 'hero',
  },
  {
    id: 'feat-design',
    icon: Palette,
    emoji: '🎨',
    color: '#a78bfa',
    colorLight: 'rgba(167,139,250,0.1)',
    colorBorder: 'rgba(167,139,250,0.2)',
    tag: 'Broadway-inspired',
    title: 'Modern Design',
    description: 'Professional layouts that look polished from the first click.',
    size: 'small',
  },
  {
    id: 'feat-templates',
    icon: LayoutTemplate,
    emoji: '📚',
    color: '#38bdf8',
    colorLight: 'rgba(56,189,248,0.1)',
    colorBorder: 'rgba(56,189,248,0.2)',
    tag: '500+ shows',
    title: 'Auto-fill Templates',
    description: 'Pick your musical — cast, songs, and billing credits fill in instantly.',
    size: 'small',
  },
  {
    id: 'feat-free',
    icon: DollarSign,
    emoji: '🆓',
    color: '#2dd4bf',
    colorLight: 'rgba(45,212,191,0.1)',
    colorBorder: 'rgba(45,212,191,0.2)',
    tag: 'Always free',
    title: 'Free to Create & Share',
    description: 'Digital flipbook programs are free forever. Only pay when you need a print PDF.',
    size: 'wide',
  },
  {
    id: 'feat-print',
    icon: Printer,
    emoji: '🖨️',
    color: '#f472b6',
    colorLight: 'rgba(244,114,182,0.1)',
    colorBorder: 'rgba(244,114,182,0.2)',
    tag: 'Save money',
    title: '25% Cheaper Printing',
    description: 'Print-optimized PDF with industry-standard bleed settings — less waste, lower cost.',
    size: 'wide',
  },
  {
    id: 'feat-share',
    icon: Share2,
    emoji: '🔗',
    color: '#34d399',
    colorLight: 'rgba(52,211,153,0.1)',
    colorBorder: 'rgba(52,211,153,0.2)',
    tag: 'No app needed',
    title: 'QR Code in the Lobby',
    description: 'One link. Audiences scan and read — no download, no printing required.',
    size: 'small',
  },
];

export default function BenefitsSection() {
  const [hovered, setHovered] = useState<string | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: heroCardRef, isVisible: heroCardVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: smallsRef, isVisible: smallsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: widesRef, isVisible: widesVisible } = useScrollReveal({ threshold: 0.1 });

  const hero = features?.[0];
  const smalls = features?.filter((f) => f?.size === 'small');
  const wides = features?.filter((f) => f?.size === 'wide');

  return (
    <section id="features" className="py-24 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-[500px] h-[500px] blur-[120px] opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.4) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[400px] h-[400px] blur-[100px] opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.4) 0%, transparent 70%)' }}
        />
      </div>
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 relative">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-14 ${headerVisible ? 'reveal-up delay-0' : 'reveal-hidden'}`}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-4"
            style={{
              background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(236,72,153,0.1))',
              border: '1px solid rgba(139,92,246,0.3)',
              borderRadius: '4px',
            }}
          >
            <span className="text-xs font-bold text-violet-300 tracking-widest uppercase">Why Ovation</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-zinc-100 mb-4">
            Everything your production needs
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            From first rehearsal to closing night — your digital program handles it all.
          </p>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* HERO card — spans 5 cols, tall */}
          <div
            ref={heroCardRef}
            className={`lg:col-span-5 relative rounded-2xl p-8 flex flex-col justify-between overflow-hidden cursor-default transition-transform duration-300 ${heroCardVisible ? 'reveal-left delay-100' : 'reveal-hidden'}`}
            style={{
              backgroundColor: hero?.colorLight,
              border: `1px solid ${hero?.colorBorder}`,
              minHeight: '340px',
              transform: hovered === hero?.id ? 'scale(1.01)' : 'scale(1)',
            }}
            onMouseEnter={() => setHovered(hero?.id)}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Big emoji watermark */}
            <div
              className="absolute -bottom-4 -right-4 text-[120px] leading-none select-none pointer-events-none"
              style={{ opacity: 0.12 }}
            >
              {hero?.emoji}
            </div>

            <div>
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-5"
                style={{ backgroundColor: `${hero?.color}22`, color: hero?.color, border: `1px solid ${hero?.color}44` }}
              >
                <hero.icon size={12} />
                {hero?.tag}
              </div>
              <h3 className="text-3xl font-extrabold text-zinc-100 mb-3 leading-tight">{hero?.title}</h3>
              <p className="text-zinc-400 text-base leading-relaxed max-w-xs">{hero?.description}</p>
            </div>

            {/* Mini step indicators */}
            <div className="flex gap-2 mt-6">
              {['Cover', 'Cast', 'Bios', 'Ads']?.map((step, i) => (
                <div
                  key={step}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: i === 0 ? hero?.color : '#71717a',
                  }}
                >
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ backgroundColor: i === 0 ? `${hero?.color}33` : 'rgba(255,255,255,0.05)', color: i === 0 ? hero?.color : '#52525b' }}
                  >
                    {i + 1}
                  </span>
                  {step}
                </div>
              ))}
            </div>
          </div>

          {/* Right column — 7 cols */}
          <div className="lg:col-span-7 flex flex-col gap-4">

            {/* Top row: 3 small cards */}
            <div ref={smallsRef} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {smalls?.map((f, idx) => (
                <div
                  key={f?.id}
                  className={`rounded-2xl p-5 flex flex-col gap-3 cursor-default transition-transform duration-300 ${smallsVisible ? `reveal-up` : 'reveal-hidden'}`}
                  style={{
                    backgroundColor: f?.colorLight,
                    border: `1px solid ${f?.colorBorder}`,
                    transform: hovered === f?.id ? 'scale(1.03)' : 'scale(1)',
                    animationDelay: smallsVisible ? `${idx * 100 + 150}ms` : '0ms',
                  }}
                  onMouseEnter={() => setHovered(f?.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div className="text-3xl leading-none">{f?.emoji}</div>
                  <div>
                    <span
                      className="text-[10px] font-bold tracking-widest uppercase"
                      style={{ color: f?.color }}
                    >
                      {f?.tag}
                    </span>
                    <h3 className="text-sm font-bold text-zinc-100 mt-0.5">{f?.title}</h3>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{f?.description}</p>
                </div>
              ))}
            </div>

            {/* Bottom row: 2 wide cards */}
            <div ref={widesRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {wides?.map((f, idx) => (
                <div
                  key={f?.id}
                  className={`rounded-2xl p-6 flex flex-col gap-4 cursor-default transition-transform duration-300 relative overflow-hidden ${widesVisible ? 'reveal-up' : 'reveal-hidden'}`}
                  style={{
                    backgroundColor: f?.colorLight,
                    border: `1px solid ${f?.colorBorder}`,
                    transform: hovered === f?.id ? 'scale(1.02)' : 'scale(1)',
                    animationDelay: widesVisible ? `${idx * 120 + 200}ms` : '0ms',
                  }}
                  onMouseEnter={() => setHovered(f?.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div
                    className="absolute top-3 right-4 text-5xl leading-none select-none pointer-events-none"
                    style={{ opacity: 0.15 }}
                  >
                    {f?.emoji}
                  </div>
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${f?.color}22` }}
                  >
                    <f.icon size={20} style={{ color: f?.color }} />
                  </div>
                  <div>
                    <span
                      className="text-[10px] font-bold tracking-widest uppercase"
                      style={{ color: f?.color }}
                    >
                      {f?.tag}
                    </span>
                    <h3 className="text-base font-bold text-zinc-100 mt-0.5">{f?.title}</h3>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-1.5">{f?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}